import type { MenuItem, MenuKeyLabel, SubMenuItem } from './NavigationSideMenu.types';
import type { LineId, PermissionAction } from '../../../features/permissions/permissionTypes';

/**
 * Removes the menu entries the connected profile cannot display (HOMCO-640):
 * - a top-level entry with a `permission` it cannot `display` is dropped;
 * - sub-entries are filtered the same way;
 * - a group left with no visible sub-entry is hidden.
 * An entry without a `permission` is always kept (e.g. Reporting — no catalogue line yet).
 */
export function filterMenuByPermissions(
  items: MenuItem[],
  can: (line: LineId, action: PermissionAction) => boolean,
): MenuItem[] {
  return items.reduce<MenuItem[]>((acc, item) => {
    if (item.permission && !can(item.permission, 'display')) return acc;

    if (item.subItems) {
      const subItems = item.subItems.filter(
        sub => !sub.permission || can(sub.permission, 'display'),
      );
      if (subItems.length === 0) return acc;
      acc.push({ ...item, subItems });
    } else {
      acc.push(item);
    }
    return acc;
  }, []);
}

// `Omit<MenuItem, 'subItems'>` drops the base `subItems?: SubMenuItem[]` before
// re-declaring it with the enriched element type. Without the Omit, TypeScript
// would produce an intersection (`SubMenuItem[] & (SubMenuItem & { selected: boolean })[]`)
// and resolve element access using the narrower `SubMenuItem` type — causing
// "Property 'selected' does not exist on type 'SubMenuItem'" at call sites.
export type ResolvedMenuItem = Omit<MenuItem, 'subItems'> & {
  selected: boolean;
  expanded: boolean;
  subItems?: (SubMenuItem & { selected: boolean })[];
};

/**
 * Derives selection and expansion state for each menu item from the current
 * route path and the list of expanded group keys.
 * Items without a `path` are never selected.
 */
export function resolveMenuItemsWithState(
  items: MenuItem[],
  currentPath: string,
  expandedKeys: MenuKeyLabel[],
): ResolvedMenuItem[] {
  // Checks exact match or proper path-segment prefix (e.g. `/employees/42` activates
  // `/employees`, but `/reporting-extra` does NOT activate `/reporting`).
  const isActive = (routePath: string) =>
    currentPath === routePath || currentPath.startsWith(routePath + '/');

  return items.map(item => ({
    ...item,
    selected: Boolean(
      (item.path && isActive(item.path)) ||
        item.subItems?.some(sub => sub.path && isActive(sub.path)),
    ),
    expanded: expandedKeys.includes(item.key),
    subItems: item.subItems?.map(sub => ({
      ...sub,
      selected: Boolean(sub.path && isActive(sub.path)),
    })),
  }));
}
